import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { displayName } from '../../../../helpers/displayName';
import { NotificationRulesService } from '../../../../services/notification-rules.service';
import { RoutingOverview } from '../../../../types/notification-rules.types';
import { teamLeaderSentence } from '../../../../components/notification-rules/routing-copy';
import { useNotificationRulesEnabled } from '../../../../components/notification-rules/use-notification-rules-enabled';

interface Leader {
    id: string;
    fullname?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
}

interface TeamLeadersCardProps {
    leaders: Leader[];
    onAddLeader?: () => void;
    onRemoveLeader?: (leaderId: string) => void;
}

export const TeamLeadersCard: React.FC<TeamLeadersCardProps> = ({
    leaders,
    onAddLeader,
    onRemoveLeader,
}) => {
    const getDisplayName = (l: Leader) =>
        // No `|| l.id` tail: resolveLeader returns a bare { id } whenever the
        // system-user lookup misses (including a swallowed failure), which put a
        // raw ObjectId on screen where a person's name belongs.
        displayName(l.fullname || [l.firstName, l.lastName].filter(Boolean).join(' ') || l.email);
    const getInitials = (l: Leader) => {
        const first = l.firstName || l.fullname?.split(' ')[0] || '?';
        const last = l.lastName || l.fullname?.split(' ').slice(1).join(' ') || '?';
        return `${first[0]}${last[0]}`.toUpperCase();
    };

    // What leaders of this team actually receive is generated from the
    // Notification Rules, not written here, so it stays true when an admin
    // changes routing or hands an alert type to a security desk.
    // Only for organizations on Notification Rules; the rest keep the
    // pre-v2 wording below, which is still how their alerts route.
    const rulesOn = useNotificationRulesEnabled();
    const [routing, setRouting] = useState<RoutingOverview | null>(null);
    useEffect(() => {
        if (!rulesOn) {
            setRouting(null);
            return undefined;
        }
        let cancelled = false;
        NotificationRulesService.fetchOverview()
            .then((o) => { if (!cancelled) setRouting(o); })
            .catch(() => { /* the card still works without the explanation */ });
        return () => { cancelled = true; };
    }, [rulesOn]);

    // Reachability comes from the routing payload, not from the leader objects
    // on this page: the groups-teams lookup does not return alertSettings, and
    // assuming its absence means "unreachable" would claim nobody can be
    // reached on every team.
    const unreachableIds = new Set((routing?.teamStructure.unreachableLeaders ?? []).map((l) => l.userId));
    const unreachableCount = leaders.filter((l) => unreachableIds.has(String(l.id))).length;
    const routingSentence = routing
        ? teamLeaderSentence(routing, {
            leaderCount: leaders.length,
            unreachableLeaderCount: unreachableCount,
        })
        : null;

    return (
        <Box
            sx={{
                backgroundColor: GT_COLORS.cardBg,
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: '10px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '14px' }}>
                <SupervisorAccountIcon sx={{ fontSize: '18px', color: GT_COLORS.primary }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Team Leaders</Typography>
                <Typography sx={{ fontSize: '12px', color: GT_COLORS.textMuted, ml: 0.5 }}>
                    ({leaders.length})
                </Typography>
            </Box>

            <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, mb: rulesOn ? '8px' : '12px' }}>
                {rulesOn
                    ? "Team Leaders manage this team's POM Users. Leaders must be System Users."
                    : "Team Leaders receive alert notifications and manage this team's POM Users. Leaders must be System Users."}
            </Typography>
            {routingSentence && (
                <Typography
                    sx={{
                        fontSize: '13px',
                        lineHeight: 1.5,
                        color: GT_COLORS.textSecondary,
                        backgroundColor: GT_COLORS.primaryLight,
                        border: `1px solid ${GT_COLORS.border}`,
                        borderRadius: '8px',
                        padding: '10px 12px',
                        mb: '12px',
                    }}
                >
                    {routingSentence}
                </Typography>
            )}

            {leaders.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '12px' }}>
                    <thead>
                        <tr>
                            <th style={{
                                textAlign: 'left', padding: '6px 10px',
                                fontSize: '11px', fontWeight: 600, color: GT_COLORS.textFaint,
                                textTransform: 'uppercase', letterSpacing: '0.5px',
                                borderBottom: `1px solid ${GT_COLORS.border}`,
                            }}>Name</th>
                            {onRemoveLeader && (
                                <th style={{
                                    width: 40, padding: '6px 10px',
                                    borderBottom: `1px solid ${GT_COLORS.border}`,
                                }} />
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {leaders.map((leader) => (
                            <tr key={leader.id}>
                                <td style={{
                                    padding: '8px 10px',
                                    borderBottom: `1px solid ${GT_COLORS.borderLight}`,
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: '50%',
                                            backgroundColor: GT_COLORS.primary, color: '#fff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '12px', fontWeight: 600, flexShrink: 0,
                                        }}>
                                            {getInitials(leader)}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                                                {getDisplayName(leader)}
                                            </Typography>
                                            <Typography sx={{ fontSize: '11px', color: GT_COLORS.textFaint }}>
                                                {leader.email || '—'}
                                            </Typography>
                                            {unreachableIds.has(String(leader.id)) && (
                                                <Typography sx={{ fontSize: '11px', color: GT_COLORS.red, fontWeight: 500 }}>
                                                    No email, text or call, so they will be skipped
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </td>
                                {onRemoveLeader && (
                                    <td style={{
                                        padding: '8px 10px',
                                        borderBottom: `1px solid ${GT_COLORS.borderLight}`,
                                    }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onRemoveLeader(leader.id)}
                                            sx={{
                                                color: GT_COLORS.textFaint,
                                                '&:hover': { color: GT_COLORS.red, bgcolor: `${GT_COLORS.red}10` },
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <Box sx={{ flex: 1 }} />

            {onAddLeader && (
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={onAddLeader}
                    sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        borderRadius: '8px',
                        ...(leaders.length === 0
                            ? {
                                '@keyframes addLeaderPulse': {
                                    '0%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}88` },
                                    '70%': { boxShadow: `0 0 0 18px ${GT_COLORS.primary}00` },
                                    '100%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}00` },
                                },
                                backgroundColor: GT_COLORS.primary,
                                color: '#fff',
                                border: `1px solid ${GT_COLORS.primary}`,
                                animation: 'addLeaderPulse 2s ease-in-out infinite',
                                fontWeight: 600,
                                '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                            }
                            : {
                                color: GT_COLORS.textMuted,
                                borderColor: GT_COLORS.borderInput,
                            }
                        ),
                    }}
                >
                    Add Leader
                </Button>
            )}
        </Box>
    );
};
